import React, { useState, useEffect } from 'react'

interface Highlight {
  id: string
  text: string
  startIndex: number
  length: number
  page: number
}

interface PDFViewerSimpleProps {
  pdfUrl: string
  highlights?: Highlight[]
  onHighlightClick?: (highlight: Highlight) => void
  onPageChange?: (page: number) => void
  currentPage?: number
  className?: string
}

export const PDFViewerSimple: React.FC<PDFViewerSimpleProps> = ({
  pdfUrl,
  highlights = [],
  onHighlightClick,
  onPageChange,
  currentPage = 1,
  className = ''
}) => {
  const [isOverlayVisible, setIsOverlayVisible] = useState(true)
  const [forceReload, setForceReload] = useState(0)

  useEffect(() => {
    // When currentPage changes, update the iframe
    if (currentPage && onPageChange) {
      onPageChange(currentPage)
    }
  }, [currentPage])

  // Force iframe to reload when currentPage changes
  useEffect(() => {
    if (currentPage && pdfUrl) {
      console.log('PDF viewer: Navigating to page', currentPage, 'for URL:', pdfUrl)
      
      // Force iframe to reload with new page parameter
      const iframe = document.querySelector('iframe[title="PDF Viewer"]') as HTMLIFrameElement
      if (iframe) {
        // Try different URL fragment formats that work with browser PDF viewers
        const baseUrl = pdfUrl.split('#')[0] // Remove any existing fragment
        const newSrc = `${baseUrl}#page=${currentPage}&zoom=page-fit`
        
        console.log('PDF viewer: Setting iframe src to:', newSrc)
        iframe.src = newSrc
        
        // Alternative: Try postMessage to the iframe (for some browsers)
        try {
          iframe.contentWindow?.postMessage({
            type: 'navigate',
            page: currentPage
          }, '*')
        } catch (e) {
          console.log('PDF viewer: postMessage failed:', e)
        }
      }
    }
  }, [currentPage, pdfUrl])

  // Show overlay when new highlights are available and force reload
  useEffect(() => {
    if (highlights.length > 0) {
      setIsOverlayVisible(true)
      // Force reload when highlights change to ensure page navigation works
      setForceReload(prev => prev + 1)
    }
  }, [highlights])

  if (!pdfUrl) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-100">
        <div className="text-center">
          <p className="text-gray-500">No PDF available</p>
        </div>
      </div>
    )
  }

  return (
    <div className={`relative h-full ${className}`}>
      {/* PDF Iframe with page parameter */}
      <iframe
        key={`${pdfUrl}-${currentPage}-${forceReload}`}
        src={`${pdfUrl}#page=${currentPage}&zoom=page-fit`}
        className="w-full h-full border-0"
        title="PDF Viewer"
        style={{ backgroundColor: '#f8fafc' }}
      />
      
      {/* Reasoning overlay — open by default, collapses into a floating bubble */}
      {highlights.length > 0 && (
        isOverlayVisible ? (
          <div className="absolute top-4 right-4 bg-purple-50 border border-purple-200 rounded-lg p-3 max-w-xs shadow-lg z-10">
            {/* Collapse-to-bubble button */}
            <button
              onClick={() => setIsOverlayVisible(false)}
              className="absolute top-2 right-2 text-purple-300 hover:text-purple-600 transition-colors"
              aria-label="Collapse reasoning"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 12H6" />
              </svg>
            </button>

            <div className="flex items-center gap-2 mb-2 pr-6">
              <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
              <div className="text-sm font-semibold text-purple-800">
                Reasoning
              </div>
            </div>
            <div className="text-xs text-purple-600 mb-1">
              Found on page {highlights[0].page}
            </div>
            {highlights[0].startIndex > 0 && (
              <div className="text-xs text-purple-400 mb-1">
                Position: {highlights[0].startIndex}-{highlights[0].startIndex + highlights[0].length}
              </div>
            )}
            <div className="mt-2 text-xs text-purple-900 bg-white p-2 rounded border border-purple-100">
              <div className="font-medium mb-1">Evidence:</div>
              <div className="italic">
                "{highlights[0].text.substring(0, 200)}{highlights[0].text.length > 200 ? '...' : ''}"
              </div>
            </div>
            <div className="mt-2 text-xs text-purple-400">
              Navigate using PDF controls
            </div>
          </div>
        ) : (
          <button
            onClick={() => setIsOverlayVisible(true)}
            className="absolute top-4 right-4 w-11 h-11 rounded-full bg-purple-500 hover:bg-purple-600 text-white shadow-lg z-10 flex items-center justify-center transition-colors"
            aria-label="Show reasoning"
            title="Reasoning"
          >
            <span className="absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-50 animate-ping"></span>
            <svg className="w-5 h-5 relative" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </button>
        )
      )}

    </div>
  )
}

export default PDFViewerSimple